/**
 * „Was 1.000 Kontakte je Werbefläche bringen" (Persona-Umbau D2, 28.10.2026): EIN Rangbalken über alle Flächen mit
 * Kontaktzahl — Webinar-Einladungen (Zuordnung über die Anmeldung) und direkte Bestell-Links (Kennung im Link) in einer
 * Liste, Hauptwert Erwarteter Wert je 1.000 Kontakte, die Menge klein darunter. Die Werte sind unverändert die
 * `wert_je_1000` derselben API-Antwort, aus der die beiden Karten darunter lesen (Test `kanaele_persona_d-db.ts` D2a).
 * Der Nachfass (Nenner: Angemeldete) steht außerhalb des Rangs, beendete Flächen am Ende; unter `grau_schwellen.anfos`
 * Anfos grau und beim Sortieren hinten. Befund aus `kern` (Direkt-Mailings gegen Heat-Ups) und `dmHaelften`.
 */
import { FC, ReactNode, useMemo } from 'react';
import { Box } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import { BefundZeile } from '../../../components/auswertungen/kennzahlTeile';
import SortableTable, { Spalte } from '../../../components/auswertungen/SortableTable';
import { unsicherFuellung, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { TERMS } from '../../../helpers/kpiDefinitions';
import type { FlaechenGrauSchwellen, FlaechenZeile, WerbeflaechenAntwort } from '../../../helpers/auswertungenData';
import { Hinweis, Laden } from '../webinare-produkte/shared';
import { rangBefund } from './befund';
import { anfosBis, anfosText, anz, datumLang, grauAnfos, letzterTag, NameZelle, tm, zeilenInfo } from './flaechenTeile';
import type { DmHaelften } from './personaDaten';
import { euroJeTausend, kompakt, useBreitePx } from './shared';

const SCHWELLEN_ERSATZ: FlaechenGrauSchwellen = { anfos: 20, anmeldungen: 50, rechtzeitig: 50, abmeldungen: 20, nach_beginn_anmeldungen: 50 };
const WARM = 'An Angemeldete (Nachfass) — anderer Nenner, nicht im Rang';
const BEENDET = 'Nicht mehr im Einsatz';
/** Unter dieser Kartenbreite nur Name und Wert; Kontakte und Anfos stehen dann in der Unterzeile. */
const SCHMAL_UNTER = 640;

/** Zuordnungsart als kleine Marke am Namen (Plan D2: „Zuordnungsart als Marke"). */
const Marke: FC<{ z: FlaechenZeile }> = ({ z }) => {
  const t = z.block === 'anmeldung' ? TERMS.zuordnungAnmeldung : TERMS.zuordnungBestellLink;
  return (
    <Box
      component="span"
      title={t.tip}
      sx={{
        display: 'inline-block',
        mt: 0.25,
        px: 0.5,
        fontSize: 11,
        lineHeight: '16px',
        color: 'text.secondary',
        border: 1,
        borderColor: 'divider',
        borderRadius: '4px',
        cursor: 'help',
        whiteSpace: 'nowrap',
      }}
    >
      {t.short}
    </Box>
  );
};

/**
 * Wert mit Balken darunter (Audit D4): `voll` = sicherer Rang (gefüllt); `unsicher` = unter der Grau-Schwelle, nur Kontur
 * mit geringer Deckkraft (`UNSICHER`) — ein gefüllter Balken behauptete einen Rang, den die Datenmenge nicht trägt;
 * `ohne` = Nachfass (anderer Nenner): kein Balken auf der gemeinsamen Achse, nur der Wert. Über der Skala läuft der
 * Balken voll und endet in „»".
 */
const WertBalken: FC<{ wert: number; max: number; farbe: string; art: 'voll' | 'unsicher' | 'ohne'; unter: ReactNode; fett: boolean }> = ({
  wert,
  max,
  farbe,
  art,
  unter,
  fett,
}) => {
  const anteil = max > 0 ? wert / max : 0;
  const ueber = anteil > 1;
  const breite = `${Math.max(0, Math.min(100, anteil * 100))}%`;
  return (
    <Box sx={{ lineHeight: 1.25, minWidth: 96 }}>
      <Box component="span" sx={{ fontWeight: fett ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>
        {euroJeTausend(wert)}
      </Box>
      {art !== 'ohne' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <Box sx={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: 'action.hover' }}>
            <Box
              data-unsicher={art === 'unsicher' ? 'kontur' : undefined}
              sx={{
                height: 6,
                borderRadius: 3,
                width: breite,
                boxSizing: 'border-box',
                ...(art === 'unsicher' ? { backgroundColor: unsicherFuellung(farbe), border: `1px solid ${farbe}` } : { backgroundColor: farbe }),
              }}
            />
          </Box>
          {ueber && (
            <Box component="span" title="Über der Skala der übrigen Flächen" sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1 }}>
              »
            </Box>
          )}
        </Box>
      )}
      <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400, whiteSpace: 'normal', mt: 0.25 }}>
        {unter}
      </Box>
    </Box>
  );
};

const RangKarte: FC<{ data: WerbeflaechenAntwort | null; fehler: string | null; haelften: DmHaelften | null }> = ({ data, fehler, haelften }) => {
  const viz = useVizStyle();
  const [breiteRef, breitePx] = useBreitePx();
  const schmal = breitePx !== null && breitePx < SCHMAL_UNTER;
  const sw = data?.grau_schwellen ?? SCHWELLEN_ERSATZ;
  const wenig = TERMS.wenigDaten.etikett.anfos;

  const { zeilen, ohneKontakte, ohneWert } = useMemo(() => {
    const rows = data?.rows ?? [];
    return {
      zeilen: rows.filter((z) => z.kontakte !== null && z.wert_je_1000 !== null),
      ohneKontakte: rows.filter((z) => z.kontakte === null),
      ohneWert: rows.filter((z) => z.kontakte !== null && z.wert_je_1000 === null),
    };
  }, [data]);
  const grau = (z: FlaechenZeile) => grauAnfos(z.anfos_ohne_vorlaeufige, sw, wenig);
  // Skala: die laufenden, sicheren Flächen im Rang — sonst machte ein grauer Ausreißer (5 Anfos) alle Balken unlesbar klein.
  const max = Math.max(0, ...zeilen.filter((z) => !z.warm && !grau(z)).map((z) => z.wert_je_1000 ?? 0));

  const spalten: Spalte<FlaechenZeile>[] = [
    {
      key: 'name',
      label: schmal ? TERMS.werbeflaeche.short : TERMS.werbeflaeche.label,
      tip: TERMS.werbeflaeche.tip,
      minWidth: schmal ? 96 : 220,
      render: (z, k) =>
        data ? (
          <Box sx={{ lineHeight: 1.3 }}>
            <NameZelle z={z} data={data} info={zeilenInfo(z, [])} sortGrund={k.sortGrund} />
            <Marke z={z} />
          </Box>
        ) : null,
      sortValue: (z) => z.label,
    },
    {
      key: 'wert1k',
      label: TERMS.wertBeiBestellung.short,
      tip: TERMS.wertJe1kFlaeche.tip,
      hinweis: `Hauptwert; darunter die Anfos (ohne vorläufig ergänzte)${schmal ? ' und die Kontakte' : ''}. Grau unter ${anz(sw.anfos)} Anfos.`,
      einheit: 'je 1.000 Kontakte',
      align: schmal ? 'right' : 'left',
      minWidth: schmal ? 110 : 220,
      render: (z) => {
        const g = grau(z);
        const unter = schmal ? `${anfosText(z.anfos_ohne_vorlaeufige)} · ${kompakt(z.kontakte)} Kontakte` : anfosText(z.anfos_ohne_vorlaeufige);
        const art = z.warm ? 'ohne' : g ? 'unsicher' : 'voll';
        return <WertBalken wert={z.wert_je_1000 ?? 0} max={max} farbe={viz.series1} art={art} unter={unter} fett={!g && !z.warm} />;
      },
      sortValue: (z) => z.wert_je_1000,
      unsicher: (z) => grau(z),
    },
    ...(schmal
      ? []
      : ([
          {
            key: 'kontakte',
            label: TERMS.kontakteZustellungen.label,
            tip: TERMS.kontakteZustellungen.tip,
            hinweis: 'Beim Nachfass sind es Angemeldete.',
            einheit: 'zugestellte Mails',
            align: 'right',
            render: (z) => <Box sx={{ color: 'text.secondary', fontSize: 13 }}>{kompakt(z.kontakte)}</Box>,
            sortValue: (z) => z.kontakte,
          },
          {
            key: 'anfos',
            label: TERMS.anfos.label,
            tip: TERMS.anfos.tip,
            hinweis: 'Ohne vorläufig ergänzte.',
            einheit: 'Anzahl',
            align: 'right',
            render: (z) => <Box sx={{ color: 'text.secondary', fontSize: 13 }}>{anz(z.anfos_ohne_vorlaeufige)}</Box>,
            sortValue: (z) => z.anfos_ohne_vorlaeufige,
          },
        ] as Spalte<FlaechenZeile>[])),
  ];

  const befund = data ? rangBefund(data.kern, haelften, sw.anfos) : null;
  const versaende = data
    ? (() => {
        const von = data.window.von;
        const bis = letzterTag(data.window.versand_bis);
        return von.slice(0, 4) === bis.slice(0, 4) ? `${tm(von)}–${datumLang(bis)}` : `${datumLang(von)}–${datumLang(bis)}`;
      })()
    : '';
  const ohneAnfos = ohneKontakte.reduce((a, z) => a + z.anfos_ohne_vorlaeufige, 0);

  return (
    <ChartCard
      title="Was 1.000 Kontakte je Werbefläche bringen"
      anker="werbeflaechen-rang"
      howToRead={
        <>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Alle Werbeflächen mit Kontaktzahl in einer Rangfolge, nach Erwartetem Wert je 1.000 Kontakte.
          </Box>{' '}
          Die Marke unter dem Namen sagt, wie die Anfos zugeordnet sind; die Einzelheiten stehen in „Webinar-Einladungen“ und „Direkte
          Bestell-Links“ weiter unten.
        </>
      }
      terms={[
        { label: TERMS.rangWerbeflaechen.label, tip: TERMS.rangWerbeflaechen.tip },
        { label: TERMS.wertJe1kFlaeche.label, tip: TERMS.wertJe1kFlaeche.tip },
        { label: TERMS.zuordnungAnmeldung.label, tip: TERMS.zuordnungAnmeldung.tip },
        { label: TERMS.zuordnungBestellLink.label, tip: TERMS.zuordnungBestellLink.tip },
        { label: TERMS.kernDmHeatUp.label, tip: TERMS.kernDmHeatUp.tip },
        { label: TERMS.direktMailingHaelften.label, tip: TERMS.direktMailingHaelften.tip },
        { label: TERMS.wenigDaten.label, tip: `${TERMS.wenigDaten.tip} Grau unter ${anz(sw.anfos)} Anfos.` },
      ]}
      source="Werbeflächen-Antwort der Karten „Webinar-Einladungen“ und „Direkte Bestell-Links“"
      freshnessKey={['newsletter_ausgaben', 'plx_orders', 'anfo_werte']}
    >
      <Box ref={breiteRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            {befund && (
              <BefundZeile
                text={'text' in befund ? befund.text : undefined}
                fehlt={'fehlt' in befund ? befund.fehlt : null}
                tip={`${TERMS.kernDmHeatUp.tip} ${TERMS.direktMailingHaelften.tip}`}
              />
            )}
            <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
              Versände {versaende} · Anfos bis {anfosBis(data)}
            </Box>
            <SortableTable
              rows={zeilen}
              columns={spalten}
              rowKey={(z) => z.key}
              initialSort={{ key: 'wert1k', dir: 'desc' }}
              abschnitt={(z) => (z.warm ? WARM : z.beendet_am ? BEENDET : null)}
              maxHeight={null}
              eng={schmal}
            />
            <Hinweis>
              Euro netto. Die Werte sind dieselben wie in den beiden Karten darunter. Für die Grundsatzfrage „Direkt-Mailing oder Heat-Up“
              taugt der Vergleich, für Abstufungen von wenigen Euro nicht — die Anfos sind verschieden zugeordnet.
              {ohneKontakte.length > 0 &&
                ` ${anz(ohneKontakte.length)} Flächen ohne Kontaktzahl (${anfosText(ohneAnfos)}) haben keinen Wert je 1.000 und stehen nur in den Karten darunter.`}
              {ohneWert.length > 0 && ` Ohne Erwarteten Wert: ${ohneWert.map((z) => z.label).join(', ')}.`}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default RangKarte;
